import React, { useEffect, useRef, useState } from 'react';
import {
  BackIconWrapper,
  Container,
  Content,
  EmptyDiv,
  Header,
  HeaderContainer,
  HeaderRow,
  HeaderText,
  Line,
  SkeletonContainer,
  StyledButton,
} from './UploadAssistedPhotos.styles';
import { ReactComponent as BackIcon } from '../../assets/icons/chevron_left.svg';
import { useNavigate } from 'react-router-dom';
import { UPLOAD_FREE_PHOTOS, UPLOAD_MEDIA, UPLOAD_PAID_PHOTOS } from '../App/routes';
import { Text18Bold, Text20Bold } from '../utils/Text/Text.styles';
import {
  ALBUM_MEDIA_TYPE,
  INFLUENCER_REQUEST_ONBOARDING_TYPE,
  MEDIA_ASSET_SALE_TAG,
  MIN_NUM_OF_FREE_PHOTOS,
  MIN_NUM_OF_PREMIUM_PHOTOS,
  UPLOAD_FILE_CONSTRAINTS,
} from '../../utils/constants';
import UnsavedChangesModal from '../modals/UnsavedChangesModal/UnsavedChangesModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteInfluencerRequestAlbumMedia,
  fetchInfluencerRequestAlbumMedia,
  uploadAssistedPhoto,
} from '../../store/slices/influencerRequest/asyncThunks';
import { notifyError, notifyInfo } from '../../utils/notify';
import UploadMediaButton from '../UploadMediaButton/UploadMediaButton';
import MediaItem from '../MediaItem/MediaItem';
import { selectUser, updateNumOfUpdatedMedia } from '../../store/slices/user/slice';
import {
  selectFetchInfluencerRequestAlbumMediaInfo,
  selectInfluencerRequestActionsPending,
} from '../../store/slices/influencerRequest/slice';
import { useConfirmationModal } from '../modals/ConfirmationModal/ConfirmationModal';
import PreviewAlbumMediaModal from '../modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';

const UploadAssistedPhotos = ({ paid }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { deleteInfluencerRequestAlbumMedia: deleteInfluencerRequestAlbumMediaPending } = useSelector(
    selectInfluencerRequestActionsPending,
  );
  const previewAlbumMediaModalRef = useRef();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const { data, pending } = useSelector(selectFetchInfluencerRequestAlbumMediaInfo);
  const [showModal, setShowModal] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingProgress, setUploadingProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  useEffect(() => {
    if (user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF) {
      navigate(paid ? `/${UPLOAD_MEDIA}/${UPLOAD_PAID_PHOTOS}` : `/${UPLOAD_MEDIA}/${UPLOAD_FREE_PHOTOS}`);
    }
  }, [user.onboardingType, navigate, paid]);

  useEffect(() => {
    setConfirmationModalLoader(deleteInfluencerRequestAlbumMediaPending);
  }, [deleteInfluencerRequestAlbumMediaPending, setConfirmationModalLoader]);

  useEffect(() => {
    dispatch(
      fetchInfluencerRequestAlbumMedia({
        kind: ALBUM_MEDIA_TYPE.PHOTO,
        tag: paid ? MEDIA_ASSET_SALE_TAG.SINGLE_PAID : MEDIA_ASSET_SALE_TAG.SINGLE_FREE,
      }),
    );
  }, [dispatch, paid]);

  const myUploadProgress = index => progress => {
    let percentage = progress;
    setUploadingProgress(prev => {
      const newProgress = [...prev];
      newProgress[index] = percentage > 99 ? 99 : percentage.toFixed(1);
      return newProgress;
    });
  };

  useEffect(() => {
    if (uploadingProgress.length && !uploadingProgress.filter(progress => progress !== 100).length) {
      dispatch(
        updateNumOfUpdatedMedia({
          numOfMedia: uploadingProgress.length,
          type: UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO,
          paid,
        }),
      );
      setLoading(false);
      navigate(`/${UPLOAD_MEDIA}`);
      notifyInfo('Photos uploaded successfully');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingProgress]);

  const onBack = () => {
    if (photos.length) {
      setShowModal(true);
    } else {
      navigate(`/${UPLOAD_MEDIA}`);
    }
  };

  const onUploadClick = () => {
    setLoading(true);
    for (const [index, photo] of photos.entries()) {
      dispatch(
        uploadAssistedPhoto({
          photo,
          type: paid ? MEDIA_ASSET_SALE_TAG.SINGLE_PAID : MEDIA_ASSET_SALE_TAG.SINGLE_FREE,
          onProgress: myUploadProgress(index),
        }),
      )
        .unwrap()
        .then(() => {
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 100;
            return newProgress;
          });
        })
        .catch(err => {
          setLoading(false);
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 0;
            return newProgress;
          });
          notifyError(err.message);
        });
    }
  };

  const minNumOfPhotos = paid ? MIN_NUM_OF_PREMIUM_PHOTOS : MIN_NUM_OF_FREE_PHOTOS;

  const minToUpload = paid
    ? user.numOfPremiumPhotos >= minNumOfPhotos
      ? 1
      : minNumOfPhotos - user.numOfPremiumPhotos
    : user.numOfFreePhotos >= minNumOfPhotos
    ? 1
    : minNumOfPhotos - user.numOfFreePhotos;

  const onPhotoDelete = (index, fileName) => {
    setPhotos(prev => prev.filter((_, ind) => index !== ind));
    setUploadErrors(prev => prev.filter(error => error.fileName !== fileName));
  };

  const onDeleteMedia = mediaId => {
    openModal(
      'Delete Media',
      `Are you sure? This action will permanently delete the media.`,
      'Delete',
      () => {
        dispatch(deleteInfluencerRequestAlbumMedia({ mediaId }))
          .unwrap()
          .then(() => {
            closeModal();
            dispatch(
              updateNumOfUpdatedMedia({
                numOfMedia: -1,
                type: UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO,
                paid,
              }),
            );
            notifyInfo('Media deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  return (
    <Container>
      <HeaderContainer>
        <BackIconWrapper onClick={onBack}>
          <BackIcon />
        </BackIconWrapper>
        <Text20Bold>Gallery</Text20Bold>
      </HeaderContainer>
      <Header>
        <HeaderRow>
          <Text18Bold>{paid ? 'Paid' : 'Free'} Photos</Text18Bold>
        </HeaderRow>
        <HeaderText>Upload at least {minNumOfPhotos} exclusive pictures. </HeaderText>
      </Header>
      <Content>
        {pending ? (
          Array.from({ length: 6 }).map((_, index) => <SkeletonContainer key={index} />)
        ) : (
          <>
            {data.map(photo => {
              return (
                <MediaItem
                  key={photo.id}
                  type={ALBUM_MEDIA_TYPE.PHOTO}
                  file={{ url: photo.thumbnailUrl }}
                  kind={photo.kind}
                  onDelete={e => {
                    onDeleteMedia(photo.id);
                    e.stopPropagation();
                  }}
                />
              );
            })}
            {!!data.length ? <Line /> : <EmptyDiv />}
          </>
        )}
      </Content>
      <Content>
        {photos.map((photo, index) => {
          return (
            <MediaItem
              key={index}
              type={ALBUM_MEDIA_TYPE.PHOTO}
              file={photo}
              progress={uploadingProgress[index]}
              onDelete={() => onPhotoDelete(index, photo.file.name)}
              uploadError={uploadErrors.find(error => error.fileName === photo.file.name)}
            />
          );
        })}
      </Content>
      <UploadMediaButton
        disabled={loading}
        type={UPLOAD_FILE_CONSTRAINTS.TYPE.PHOTO}
        onChange={files => setPhotos([...photos, ...files])}
        setError={setUploadErrors}
      />
      <StyledButton
        title={`Save ${minToUpload > photos.length ? `(at least ${minToUpload - photos.length})` : ''}`}
        isLoading={loading}
        onClick={onUploadClick}
        disabled={minToUpload > photos.length || uploadErrors.length}
      />
      <UnsavedChangesModal
        show={showModal}
        setShow={setShowModal}
        onDiscard={() => navigate(`/${UPLOAD_MEDIA}`)}
        onSave={() => {
          onUploadClick();
          setShowModal(false);
        }}
      />
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={selectedMedia?.kind}
        url={selectedMedia?.url}
        setSelectedUrl={setSelectedMedia}
      />
    </Container>
  );
};

export default UploadAssistedPhotos;
